import { useEffect } from 'react';
import { useStrings } from '@/i18n/Localization';
import { MAX_PINS } from '@/protocol/limits';
import { useLiveQuery } from '@/store/hooks';
import { listPins } from '@/store/localStore';
import { db } from '@/store/db';
import type { Hex } from '@/store/ids';
import type { MessageRecord, PinRecord } from '@/store/types';
import type { WorkerClient } from '@/transport/workerClient';
import styles from '@/components/panels.module.css';

export interface PinnedPanelProps {
  worker: WorkerClient | null;
  conversationId: Hex;
  canUnpin: boolean;
  onOpen: (messageId: string) => void;
  onUnpin: (messageId: string) => void;
  onClose: () => void;
}

/**
 * The pinned messages of one conversation.
 *
 * The list is read from LocalStore and refreshed from `ListPins` when the panel
 * opens. Both are needed: the store answers offline and instantly, and the
 * server call is what discovers a pin another device made while this one was
 * away — a pin is not carried on any event this client subscribes to.
 */
export function PinnedPanel(props: PinnedPanelProps) {
  const { t, formatRelativeDay } = useStrings();

  const pins = useLiveQuery<PinRecord[]>(
    () => listPins(props.conversationId),
    [props.conversationId],
    [],
  );

  const messages = useLiveQuery<MessageRecord[]>(
    async () => {
      const rows = await db.messages
        .where('[conversationId+id]')
        .anyOf(pins.map((pin) => [props.conversationId, pin.messageId] as [string, string]))
        .toArray();
      return rows;
    },
    [props.conversationId, pins.map((pin) => pin.messageId).join(',')],
    [],
  );

  useEffect(() => {
    void props.worker?.call('list-pins', {
      conversationId: props.conversationId,
      cursor: null,
      limit: MAX_PINS,
    }).catch(() => {
      // Offline. The stored pins are still shown; nothing here needs to shout
      // about a refresh that could not happen.
    });
  }, [props.worker, props.conversationId]);

  return (
    <section className={styles.panel} aria-label={t('pin.title')}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>{t('pin.title')}</h2>
        <button type="button" className={styles.action} onClick={props.onClose}>
          {t('pin.close')}
        </button>
      </div>
      <div className={styles.panelBody}>
        {messages.length === 0 ? (
          <p className={styles.empty}>{t('pin.empty')}</p>
        ) : (
          <ul className={styles.list}>
            {messages.map((message) => (
              <li key={message.id} className={styles.row}>
                <button
                  type="button"
                  className={styles.rowBody}
                  style={{ textAlign: 'start' }}
                  onClick={() => {
                    props.onOpen(message.id);
                  }}
                >
                  <span className={styles.rowTitle} dir="auto">
                    {message.content.text || t('inbox.noMessages')}
                  </span>
                  <span className={styles.rowMeta}>{formatRelativeDay(message.createdAt)}</span>
                </button>
                {props.canUnpin ? (
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.action}
                      onClick={() => {
                        props.onUnpin(message.id);
                      }}
                    >
                      {t('pin.remove')}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
